import { useState, useMemo, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CalendarDays, Check, Loader2, Users, School, GraduationCap, ArrowRight } from "lucide-react";
import axios from "axios";
import { SchoolLookup } from "../components/SchoolLookup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date(0);
  const clean = dateStr.replace("Z", "").replace(/[+-]\d{2}:\d{2}$/, "");
  const [datePart, timePart] = clean.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [h, min, s] = (timePart || "00:00:00").split(":").map(Number);
  return new Date(y, m - 1, d, h, min, s || 0);
};

export default function OperatorRegistrationPage() {
  const [adminKey, setAdminKey] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [authError, setAuthError] = useState("");

  const [operatorId, setOperatorId] = useState("");
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", ico: "", school_type: "zs", webinar_date: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [registeredCount, setRegisteredCount] = useState(0);

  const handleAuth = (e) => {
    e.preventDefault();
    if (adminKey.length > 10) {
      setAuthorized(true);
      setAuthError("");
    } else {
      setAuthError("Neplatný klíč.");
    }
  };

  const [msDates, setMsDates] = useState([]);
  const [zsDates, setZsDates] = useState([]);

  useEffect(() => {
    axios.get(`${API}/webinar-dates`).then(res => {
      if (Array.isArray(res.data)) {
        setMsDates(res.data.filter(d => d.school_type === "ms"));
        setZsDates(res.data.filter(d => d.school_type === "zs"));
      }
    }).catch(() => {});
  }, []);

  const allDates = formData.school_type === "zs" ? zsDates : msDates;
  const upcomingDates = useMemo(() => {
    const now = new Date();
    return allDates.filter(w => parseLocalDate(w.date) > now);
  }, [allDates]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, webinar_date: "" }));
  }, [formData.school_type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!operatorId.trim()) { setError("Vyplňte ID operátora."); return; }
    if (parseInt(operatorId) < 1 || parseInt(operatorId) > 99 || isNaN(parseInt(operatorId))) { setError("ID operátora musí být číslo 1–99."); return; }
    if (!formData.name.trim() || !formData.email.trim() || !formData.ico.trim()) { setError("Vyplňte jméno, email a IČO."); return; }
    if (!formData.email.includes("@")) { setError("Neplatný email."); return; }
    if (!formData.webinar_date && upcomingDates.length > 0) { setError("Vyberte termín."); return; }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/operator-register`, {
        admin_key: adminKey,
        operator_id: operatorId,
        ...formData
      });
      if (res.data.error) { setError(res.data.error); }
      else {
        setSuccess(true);
        setRegisteredCount(prev => prev + 1);
        setTimeout(() => {
          setSuccess(false);
          setFormData({ name: "", email: "", phone: "", ico: "", school_type: formData.school_type, webinar_date: "" });
        }, 2000);
      }
    } catch (err) { setError("Chyba při odesílání."); }
    finally { setLoading(false); }
  };

  // Auth screen
  if (!authorized) {
    return (
      <div className="min-h-screen bg-[#F4F7FC] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl border border-[#E8EDF5] p-8 max-w-sm w-full">
          <div className="w-12 h-12 rounded-xl bg-[#0049B3]/10 flex items-center justify-center mx-auto mb-5">
            <Users className="w-6 h-6 text-[#0049B3]" />
          </div>
          <h1 className="text-xl font-bold text-[#0F1B3D] text-center mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Operátorská registrace</h1>
          <p className="text-sm text-[#6B7A99] text-center mb-6">Zadejte přístupový klíč</p>
          <form onSubmit={handleAuth} className="space-y-4">
            <Input type="password" placeholder="Přístupový klíč" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} className="rounded-xl border-[#E8EDF5] py-5" />
            {authError && <p className="text-sm text-red-500">{authError}</p>}
            <Button type="submit" className="w-full bg-[#0049B3] hover:bg-[#003380] text-white rounded-xl py-5 font-bold">Přihlásit se</Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7FC]">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#0F1B3D]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Operátorská registrace na ukázku</h1>
            <p className="text-sm text-[#6B7A99] mt-1">Registrujete účastníky jménem operátora</p>
          </div>
          <div className="bg-emerald-100 text-emerald-700 font-bold px-4 py-2 rounded-xl text-sm">
            Přihlášeno: {registeredCount}
          </div>
        </div>

        {/* Success */}
        {success && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-3">
            <Check className="w-6 h-6 text-emerald-600" />
            <span className="font-semibold text-emerald-700">Účastník úspěšně registrován!</span>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#E8EDF5] shadow-sm p-6 lg:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Operator ID */}
            <div className="bg-[#F8FAFF] rounded-xl p-4 border border-[#E8EDF5]">
              <Label className="text-sm font-bold text-[#0049B3]">ID operátora *</Label>
              <Input type="number" min="1" max="99" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} placeholder="1–99" className="mt-2 rounded-xl border-[#0049B3]/20 py-5 text-lg font-bold text-center" />
            </div>

            {/* School type toggle */}
            <div>
              <Label className="text-sm font-semibold text-[#0F1B3D] mb-2 block">Typ školy *</Label>
              <div className="flex gap-3">
                <button type="button" onClick={() => setFormData({ ...formData, school_type: "zs", webinar_date: "" })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm transition-all ${formData.school_type === "zs" ? "bg-[#0049B3] text-white shadow-md" : "bg-[#F4F7FC] text-[#6B7A99] border border-[#E8EDF5]"}`}>
                  <GraduationCap className="w-4 h-4" /> ZŠ
                </button>
                <button type="button" onClick={() => setFormData({ ...formData, school_type: "ms", webinar_date: "" })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm transition-all ${formData.school_type === "ms" ? "bg-[#0049B3] text-white shadow-md" : "bg-[#F4F7FC] text-[#6B7A99] border border-[#E8EDF5]"}`}>
                  <School className="w-4 h-4" /> MŠ
                </button>
              </div>
            </div>

            {/* Participant info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-[#0F1B3D]">Jméno účastníka *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Jan Novák" className="rounded-xl border-[#E8EDF5] py-5" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-[#0F1B3D]">Škola (IČO nebo název) *</Label>
                <SchoolLookup onSelect={({ ico, school_name }) => setFormData({ ...formData, ico, school_name: school_name || "" })} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-[#0F1B3D]">Email *</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="jan@skola.cz" className="rounded-xl border-[#E8EDF5] py-5" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-[#0F1B3D]">Telefon</Label>
                <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+420..." className="rounded-xl border-[#E8EDF5] py-5" />
              </div>
            </div>

            {/* Webinar date */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#0F1B3D]">Termín ukázky *</Label>
              {upcomingDates.length === 0 ? (
                <p className="text-sm text-[#9CA3AF] bg-[#F8FAFF] rounded-xl p-4 border border-[#E8EDF5]">Žádné nadcházející termíny pro {formData.school_type === "zs" ? "ZŠ" : "MŠ"}.</p>
              ) : (
                <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                  {upcomingDates.map((w, idx) => (
                    <button key={idx} type="button" onClick={() => setFormData({ ...formData, webinar_date: w.date })}
                      className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 border text-left transition-all ${formData.webinar_date === w.date ? "bg-[#0049B3]/10 border-[#0049B3]/30 text-[#0049B3]" : "bg-[#F8FAFF] border-[#E8EDF5] text-[#3D4F6F] hover:bg-[#F0F4FA]"}`}>
                      <CalendarDays className={`w-4 h-4 flex-shrink-0 ${formData.webinar_date === w.date ? "text-[#0049B3]" : "text-[#9CA3AF]"}`} />
                      <span className="text-sm font-semibold">{w.dayLabel}</span>
                      <span className="text-sm">{w.timeLabel}</span>
                      {formData.webinar_date === w.date && <Check className="w-4 h-4 text-[#0049B3] ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {error && <p className="text-sm text-red-500 font-medium">{error}</p>}

            <Button type="submit" disabled={loading} size="lg" className="w-full bg-[#0049B3] hover:bg-[#003380] text-white rounded-xl font-bold py-6 text-base">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>Registrovat účastníka<ArrowRight className="ml-2 w-5 h-5" /></>)}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
